/* global ClipboardItem */
import React, {useEffect, useState, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import classnames from 'classnames';
import RaisedButton from "material-ui/RaisedButton";
import FlatButton from "material-ui/FlatButton";
import './style.css';
import theme from "../SlateViews/theme";
import {
  notificationTypes,
} from "../../config/constants";
import {Tooltip} from "@material-ui/core";
import { fieldCellCopyText } from 'components/FieldColumns/fieldTypes';
import BasicDialog from 'components/Dialogs/BasicDialog';
import { formatColumnHeaderText } from 'components/FieldColumns/columnHeader';

// Material Symbol component for modern icons
const MaterialSymbol = ({ name, style = {} }) => (
  <span
    className="material-symbols-outlined"
    style={{ fontSize: 20, lineHeight: 1, ...style }}
  >
    {name}
  </span>
);

// Progress ring component for loading state
const ProgressRing = ({ value, style = {} }) => {
  const percentage = Math.min(100, Math.max(0, value));
  const radius = 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      style={{ fontSize: 24, lineHeight: 1, verticalAlign: 'middle', ...style }}
    >
      <circle cx="12" cy="12" r={radius} fill="none" stroke="#dbeafe" strokeWidth="3" />
      <circle
        cx="12"
        cy="12"
        r={radius}
        fill="none"
        stroke="#3b82f6"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 12 12)"
      />
    </svg>
  );
};

export const getFormattedValue = (value, type, displayType) =>
  fieldCellCopyText(value, { type, displayType });

export const CopyTableButton = ({
  currentSlateView,
  slates,
  showNotification,
  fetchMoreSlates,
  className,
  rowCount,
  disabled
}) => {
  const {t} = useTranslation();
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const pendingCopyResolverRef = useRef(null);
  const slatesLength = slates ? slates.length : 0;
  const prevSlatesLengthRef = useRef(slatesLength);

  // Fetch data progressively until all rows are loaded
  useEffect(() => {
    if (!isFetchingData || !slates || !currentSlateView) {
      return;
    }

    const currentCount = slates.length;
    const prevCount = prevSlatesLengthRef.current;
    prevSlatesLengthRef.current = currentCount;

    // Only act when slates.length actually changed (new data arrived)
    if (currentCount === prevCount) {
      return;
    }

    if (currentCount < rowCount) {
      fetchMoreSlates();
    } else {
      // All data loaded - resolve the pending copy promise
      setIsFetchingData(false);
      if (pendingCopyResolverRef.current) {
        pendingCopyResolverRef.current(buildTableText());
        pendingCopyResolverRef.current = null;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingData, slatesLength]);

  const buildTableText = () => {
    const columns = currentSlateView.columns;
    const header = columns.map(col => formatColumnHeaderText(col)).join('\t');
    const rows = slates.map(slate =>
      slate.columns.map((cell, cellIndex) =>
        getFormattedValue(cell.value, columns[cellIndex].type, columns[cellIndex].displayType)
      ).join('\t')
    );
    return header + '\n' + rows.join('\n');
  };

  // Copy text to clipboard (used when data is already loaded)
  const copyToClipboard = async (tableText) => {
    const text = tableText || buildTableText();
    let success = false;

    try {
      await navigator.clipboard.writeText(text);
      success = true;
    } catch (err) {
      // Fallback for contexts where Clipboard API fails
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        success = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (fallbackErr) {
        console.error('[CopyTable] Copy fallback failed:', fallbackErr);
      }
    }

    showNotification(
      success ? notificationTypes.SUCCESS : notificationTypes.ERROR,
      t(success ? 'notifications:copyTableToClipboardSuccessfully' : 'notifications:copyTableToClipboardUnsuccessfully'),
    );
    return success;
  };

  // Fetch all data and return a promise that resolves to table text
  const fetchAllDataAndBuildText = () => {
    if (slates.length >= rowCount) {
      return Promise.resolve(buildTableText());
    }

    return new Promise((resolve) => {
      pendingCopyResolverRef.current = resolve;
      setIsFetchingData(true);
      fetchMoreSlates();
    });
  };

  const handleClick = async () => {
    if (isFetchingData) {
      setShowCancelModal(true);
      return;
    }

    // If all data already loaded, copy directly
    if (rowCount <= slates.length) {
      await copyToClipboard();
      return;
    }

    // Use ClipboardItem with promise for async copy (preserves user gesture)
    try {
      const textPromise = fetchAllDataAndBuildText();
      const clipboardItem = new ClipboardItem({
        'text/plain': textPromise.then(text => new Blob([text], { type: 'text/plain' }))
      });
      await navigator.clipboard.write([clipboardItem]);
      showNotification(notificationTypes.SUCCESS, t('notifications:copyTableToClipboardSuccessfully'));
    } catch (err) {
      console.error('[CopyTable] Copy failed:', err);
      showNotification(notificationTypes.ERROR, t('notifications:copyTableToClipboardUnsuccessfully'));
    }
  };

  const handleCancelCopy = () => {
    setIsFetchingData(false);
    pendingCopyResolverRef.current = null;
    setShowCancelModal(false);
  };

  const cancelModalActions = (
    <>
      <FlatButton
        label={t('btn:continue')}
        onClick={() => setShowCancelModal(false)}
      />
      <FlatButton
        label={t('btn:cancelOperation')}
        primary
        onClick={handleCancelCopy}
      />
    </>
  );

  const getButtonIcon = () => {
    if (isFetchingData) {
      return <ProgressRing value={Math.round(100 * slates.length / rowCount)} />;
    }
    return <MaterialSymbol name="content_copy" style={theme.slateViewActionIconStyle} />;
  };

  return (
    <>
      <Tooltip placement={'top'} title={t('tooltips:copyToClipboard')}>
        <div className={classnames('copy-button-wrapper', {
          'copy-button-active': isFetchingData,
        })}>
          <RaisedButton
            icon={getButtonIcon()}
            disabled={disabled}
            style={theme.slateViewActionStyle}
            backgroundColor={undefined}
            className={className}
            onClick={handleClick}
          />
        </div>
      </Tooltip>

      <BasicDialog
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title={t('slateView:cancelCopyTitle')}
        actions={cancelModalActions}
        maxWidth="xs"
      >
        <div style={{ padding: '16px 24px' }}>
          {t('slateView:cancelCopyMessage')}
        </div>
      </BasicDialog>
    </>
  );
};
